import { APIProvider, authController } from "../../../../AppController";
import { APIRoutes } from "../../../../config";
import {
  IHealthScribeRepository,
  IStartRecordingRequest,
  IStartRecordingResponse,
  IStopRecordingRequest,
  IStopRecordingResponse,
  IGetSessionRequest,
  IGetSessionResponse,
  IListSessionsRequest,
  ITranscriptionSaveRequest,
  IListTranscriptionsRequest,
  IListTranscriptionsResponse,
  ICompleteTranscriptionRequest,
  ICompleteTranscriptionResponse,
  IS3MultipartUploadInit,
  IS3MultipartUploadInitResponse,
  IS3MultipartUploadPart,
  IS3MultipartUploadPartResponse,
  IS3MultipartUploadComplete,
  IS3MultipartUploadCompleteResponse,
  IS3MultipartUploadAbort,
  IS3UploadCompleteAudioRequest,
  IS3UploadCompleteAudioResponse,
} from "../../../core/ports/HealthScribe";

const env = import.meta.env;

export const HealthScribeRepositoryFactory = ({
  apiProvider,
  authService,
}: {
  apiProvider: typeof APIProvider;
  authService: typeof authController;
}): IHealthScribeRepository => {
  const getAuthHeaders = async () => ({
    Authorization: `Bearer ${await authService.getAuthenticationToken()}`,
    "Content-Type": "application/json",
  });

  const startRecording = async (
    request: IStartRecordingRequest
  ): Promise<IStartRecordingResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeStartRecording,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to start recording:",
        error
      );
      throw error;
    }
  };

  const stopRecording = async (
    request: IStopRecordingRequest
  ): Promise<IStopRecordingResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeStopRecording,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to stop recording:",
        error
      );
      throw error;
    }
  };

  const getSession = async (
    request: IGetSessionRequest
  ): Promise<IGetSessionResponse> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeGetSession
        .replace("{clinic_id}", request.clinic_id.toString())
        .replace("{session_id}", request.session_id);
      const response = await apiProvider.GET(url, {}, headers);
      return response;
    } catch (error) {
      console.error("[HealthScribe Repository] Failed to get session:", error);
      throw error;
    }
  };

  const listSessions = async (
    request: IListSessionsRequest
  ): Promise<string> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeListSessions.replace(
        "{clinic_id}",
        request.clinic_id.toString()
      );
      const queryParams = new URLSearchParams();
      if (request.patient_id)
        queryParams.append("patient_id", request.patient_id.toString());
      if (request.limit) queryParams.append("limit", request.limit.toString());
      if (request.offset)
        queryParams.append("offset", request.offset.toString());

      const finalUrl = queryParams.toString()
        ? `${url}?${queryParams.toString()}`
        : url;
      const response = await apiProvider.GET(finalUrl, {}, headers);
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to list sessions:",
        error
      );
      throw error;
    }
  };

  // Transcription Save methods
  const saveTranscription = async (
    request: ITranscriptionSaveRequest
  ): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      await apiProvider.POST(
        APIRoutes.healthScribeTranscriptionSave,
        request,
        headers
      );
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to save transcription:",
        error
      );
      throw error;
    }
  };

  const batchSaveTranscriptions = async (
    request: ITranscriptionSaveRequest
  ): Promise<{ narrative?: string }> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeTranscriptionBatchSave,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to batch save transcriptions:",
        error
      );
      throw error;
    }
  };

  // S3 Multipart Upload methods
  const initS3MultipartUpload = async (
    request: IS3MultipartUploadInit
  ): Promise<IS3MultipartUploadInitResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeS3UploadInit,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to init S3 multipart upload:",
        error
      );
      throw error;
    }
  };

  const uploadS3MultipartPart = async (
    request: IS3MultipartUploadPart
  ): Promise<IS3MultipartUploadPartResponse> => {
    try {
      const formData = new FormData();
      formData.append("session_id", request.session_id);
      formData.append("clinic_id", request.clinic_id.toString());
      formData.append("upload_id", request.upload_id);
      formData.append("part_number", request.part_number.toString());
      formData.append("file", request.file);

      // Use raw fetch for FormData since APIProvider might not handle it properly
      const authToken = await authService.getAuthenticationToken();

      const response = await fetch(
        `${env.VITE_API_GATEWAY_URL}${APIRoutes.healthScribeS3UploadPart}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to upload S3 multipart part:",
        error
      );
      throw error;
    }
  };

  const completeS3MultipartUpload = async (
    request: IS3MultipartUploadComplete
  ): Promise<IS3MultipartUploadCompleteResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.POST(
        APIRoutes.healthScribeS3UploadComplete,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to complete S3 multipart upload:",
        error
      );
      throw error;
    }
  };

  const abortS3MultipartUpload = async (
    request: IS3MultipartUploadAbort
  ): Promise<void> => {
    try {
      const headers = await getAuthHeaders();
      const url = APIRoutes.healthScribeS3UploadAbort
        .replace("{clinic_id}", request.clinic_id.toString())
        .replace("{session_id}", request.session_id)
        .replace("{upload_id}", request.upload_id);
      await apiProvider.DELETE(url, {}, headers);
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to abort S3 multipart upload:",
        error
      );
      throw error;
    }
  };

  const uploadCompleteAudio = async (
    request: IS3UploadCompleteAudioRequest
  ): Promise<IS3UploadCompleteAudioResponse> => {
    try {
      const formData = new FormData();
      formData.append("session_id", request.session_id);
      formData.append("clinic_id", request.clinic_id.toString());
      formData.append("filename", request.filename);
      formData.append("content_type", request.content_type);
      formData.append("file", request.audio_file);

      const response = await apiProvider.POST(
        APIRoutes.healthScribeS3UploadCompleteAudio,
        formData,
        {
          Authorization: `Bearer ${await authService.getAuthenticationToken()}`,
          "Content-Type": "multipart/form-data",
        }
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to upload complete audio:",
        error
      );
      throw error;
    }
  };

  // New Transcription List and Complete Methods
  const listTranscriptions = async (
    request: IListTranscriptionsRequest
  ): Promise<IListTranscriptionsResponse> => {
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      queryParams.append("patient_id", request.patient_id.toString());
      queryParams.append("clinic_id", request.clinic_id.toString());

      const url = `${
        APIRoutes.healthScribeTranscriptionList
      }?${queryParams.toString()}`;
      const response = await apiProvider.GET(url, {}, headers);
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to list transcriptions:",
        error
      );
      throw error;
    }
  };

  const completeTranscriptionGenerateNarrative = async (
    request: ICompleteTranscriptionRequest
  ): Promise<ICompleteTranscriptionResponse> => {
    try {
      const headers = await getAuthHeaders();
      const response = await apiProvider.PUT(
        APIRoutes.healthScribeTranscriptionCompleteGenerateNarrative,
        request,
        headers
      );
      return response;
    } catch (error) {
      console.error(
        "[HealthScribe Repository] Failed to complete transcription generate narrative:",
        error
      );
      throw error;
    }
  };

  return {
    startRecording,
    stopRecording,
    getSession,
    listSessions,
    saveTranscription,
    batchSaveTranscriptions,
    listTranscriptions,
    completeTranscriptionGenerateNarrative,
    initS3MultipartUpload,
    uploadS3MultipartPart,
    completeS3MultipartUpload,
    abortS3MultipartUpload,
    uploadCompleteAudio,
  };
};
